"use client";

import { BaseRadio, Checkbox } from "@offstreetparking/design-system/forms";
import { classNames } from "@offstreetparking/design-system/utils";
import { EditVehicleButton } from "./EditVehicleButton";
import { VehicleLabel } from "./VehicleLabel";
import { type ParkerVehicle, vehicleKey } from "./parker-vehicle";

type VehicleListProps = {
	parkerVehicles: (ParkerVehicle & { isDisabled?: true })[];
	selectedVehicles: ParkerVehicle[];
	maxVehicles: number;
	isSingleSelect: boolean;
	onChange: (args: {
		checked: boolean;
		option: ParkerVehicle;
	}) => void;
	onEdit: (vehicle: ParkerVehicle) => void;
};

export const VehicleList = ({
	parkerVehicles,
	selectedVehicles,
	maxVehicles,
	isSingleSelect,
	onChange,
	onEdit,
}: VehicleListProps) => {
	if (isSingleSelect) {
		const selectedKey = selectedVehicles[0] ? vehicleKey(selectedVehicles[0]) : undefined;
		return (
			<BaseRadio
				name="vehicle-select"
				value={selectedKey}
				onChange={(val) => {
					const vehicle = parkerVehicles.find((v) => vehicleKey(v) === val);
					if (vehicle) {
						onChange({
							checked: true,
							option: vehicle,
						});
					}
				}}
				size="lg"
				className="space-y-4"
				itemClassName="items-stretch gap-0 rounded-lg border border-neutral bg-white has-data-checked:border-primary has-data-checked:bg-primary-faded"
				radioClassName="cursor-pointer p-4"
				options={parkerVehicles.map((option) => ({
					value: vehicleKey(option),
					content: <VehicleLabel vehicle={option} />,
					adjacent: option.isDisabled ? null : (
						<div className="flex shrink-0 items-center pr-4">
							<EditVehicleButton vehicle={option} onClick={() => onEdit(option)} />
						</div>
					),
				}))}
			/>
		);
	}
	return (
		<div className="space-y-4">
			{parkerVehicles.map((option) => {
				const optionKey = vehicleKey(option);
				const isSelected = selectedVehicles.some((vehicle) => vehicleKey(vehicle) === optionKey);
				const isDisabled = !isSelected && selectedVehicles.length >= maxVehicles;
				return (
					<div
						key={optionKey}
						className={classNames(
							"flex items-center gap-4 rounded-lg border p-4",
							isSelected ? "border-primary bg-primary-faded" : "border-neutral bg-white",
						)}
					>
						<Checkbox
							name={`vehicle-${optionKey}`}
							value={isSelected}
							onChange={(checked) =>
								onChange({
									checked,
									option,
								})
							}
							label={<VehicleLabel vehicle={option} />}
							labelClassName={classNames(
								"flex min-w-0 flex-1 items-center gap-6",
								isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
							)}
							containerClassName="min-w-0 flex-1"
							isDisabled={isDisabled}
						/>
						{!option.isDisabled && <EditVehicleButton vehicle={option} onClick={() => onEdit(option)} />}
					</div>
				);
			})}
		</div>
	);
};
